
import { GetRequest, PostRequest, PutRequest } from "../../Utils/hooks/ApiHandler";

const API_BASE = "https://dev.pugcrm.com/crmapi/teams";

// createOutlookMeeting/updateOutlookMeeting post to the exact same
// lead/createMeeting & lead/updateMeeting endpoints ProjectMeeting.jsx uses.
// That reference sends "timezone" (lowercase) as a full object, e.g.
// { value: "Asia/Kolkata", label: "...", offset: 5.5, abbrev: "IST",
//   altName: "India Standard Time" } - not a plain string under "timeZone".
// Our Outlook form has no timezone picker, so we always send this same
// default IST object the reference falls back to.
const DEFAULT_TIMEZONE = {
  value: "Asia/Kolkata",
  label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
  offset: 5.5,
  abbrev: "IST",
  altName: "India Standard Time",
};

// Our own DB stores invitees as full contact objects:
// [{ id, email, fullName, module }]
// The Microsoft Graph API (which schedule_teams/update_teams talk to under
// the hood) does NOT accept that shape - it needs the "attendees" shape:
// [{ emailAddress: { address, name }, type: "required" }]
// This converts our DB shape into the Graph shape right before we hit the
// Teams endpoints, without touching what actually gets saved to the DB.
function mapInviteesForGraph(invitees = []) {
  const list = Array.isArray(invitees) ? invitees : [invitees];
  return list
    .filter(Boolean)
    .map((inv) => {
      // Backwards compatible: if someone still passes plain email strings,
      // handle that too instead of throwing.
      if (typeof inv === "string") {
        return {
          emailAddress: { address: inv, name: inv },
          type: "required",
        };
      }
      return {
        emailAddress: {
          address: inv.email || "",
          name: inv.fullName || inv.label || "",
        },
        type: "required",
      };
    })
    .filter((a) => a.emailAddress.address);
}

export function connectOutlook(userId) {
  if (!userId) {
    throw new Error("connectOutlook requires a userId");
  }
  window.location.href = `${API_BASE}/login?userId=${encodeURIComponent(userId)}`;
}

export async function checkOutlookStatus(userId) {
  if (!userId) return { connected: false, email: null, name: null };
  try {
    const res = await fetch(
      `${API_BASE}/status?userId=${encodeURIComponent(userId)}`,
    );
   if (!res.ok) return { connected: false, email: null, name: null };
    const data = await res.json();
    return {
      connected: !!data.connected,
      email: data.email || null,
      name: data.name || null,
    };
  } catch {
   return { connected: false, email: null, name: null };
  }
}
export async function logoutOutlook(userId) {
  if (!userId) return;
  try {
    await fetch(`${API_BASE}/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
  } catch {
    // even if the network call fails, caller should still clear local UI state
  }
}
export async function generateTeamsLink({
  userId,
  title,
  description,
  startISO,
  endISO,
  invitees = [],
  timeZone = "India Standard Time",
  headers,
  extras
}) {
  if (!userId) {
    throw new Error("generateTeamsLink requires a userId");
  }
    const payload =JSON.stringify({
      userId,
      title,
      description,
      startDate: startISO,
      endDate: endISO,
      // Graph API attendee format - see mapInviteesForGraph.
      invitees: mapInviteesForGraph(invitees),
      timeZone,
    })
  const res = await PostRequest(`teams/schedule_teams`,payload,{headers},extras);
  if (res.status === 401) {
    const err = new Error("Outlook session expired");
    err.code = 401;
    throw err;
  }
  const data = await res;
  let meeting_type=""
  if (data?.joinUrl == null){
    meeting_type ="calender_meeting"
  }
  return {
    meetLink: data?.joinUrl || data?.webLink || null,
    eventId: data?.eventId || null,
    meeting_type
  };
}
export async function updateTeamsEvent({
  eventId,
  userId,
  title,
  description,
  startISO,
  endISO,
  invitees = [],
  timeZone = "India Standard Time",
}) {
  if (!eventId) {
    throw new Error("updateTeamsEvent requires an eventId");
  }
  if (!userId) {
    throw new Error("updateTeamsEvent requires a userId");
  }
  const res = await fetch(
    `${API_BASE}/update_teams/${encodeURIComponent(eventId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        title,
        description,
        startDate: startISO,
        endDate: endISO,
        // Graph API attendee format - see mapInviteesForGraph.
        invitees: mapInviteesForGraph(invitees),
        timeZone,
      }),
    },
  );
  if (res.status === 401) {
    const err = new Error("Outlook session expired");
    err.code = 401;
    throw err;
  }
  if (!res.ok) {
    const err = new Error("Could not update Teams meeting");
    err.code = res.status;
    throw err;
  }
  const data = await res.json();
  let meeting_type = "";
  if (data?.joinUrl == null) {
    meeting_type = "calender_meeting";
  }
  return {
    meetLink: data?.joinUrl || data?.webLink || null,
    eventId: data?.eventId || eventId,
    meeting_type,
  };
}

export function formatDateDMY(d) {
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}
function mapMeetingToCalendarEvent(m) {
  if (!m?.meeting_date) return null;
  const fromTime = (m.meeting_from || "00:00").slice(0, 5);
  const toTime = (m.meeting_to || m.meeting_from || "00:00").slice(0, 5);
  let invitees = [];
  try {
    invitees =
      typeof m.invitees === "string"
        ? JSON.parse(m.invitees)
        : m.invitees || [];
  } catch {
    invitees = [];
  }
  return {
    id: m.id,
    title: m.meeting_title || "(No title)",
    start: `${m.meeting_date}T${fromTime}:00`,
    end: `${m.meeting_date}T${toTime}:00`,
    extendedProps: {
      htmlLink: m.meeting_link || null,
      meetLink: m.meeting_link || null,
      description: m.description || "",
      meetingType: m.meeting_type,
      status: m.meeting_status,
      address: m.address,
      moduleId: m.module_id,
      moduleName: m.module_name,
      invitees,
      assignTo: m.assign_to || null,
      source: "outlook",
      graphEventId: m.event_id || m.id,
    },
  };
}
export async function createOutlookMeeting({
  userId,
  customer_id,
  moduleId,
  eventId,
  moduleName = "calendar",
  title,
  description,
  date,
  startTime,
  endTime,
  meetLink,
  meetingType,
  invitees = [],
  assignTo,
  headers,
  extras,
}) {
  if (!userId || !customer_id) {
    throw new Error("createOutlookMeeting requires userId and customer_id");
  }
  const payload = {
    module_id: moduleId || userId,
    module_name: "lead",
    customer_id,
   event_id: eventId || "",
    user_login_id: userId,
    assign_to: assignTo || userId,
    meeting_title: title,
    meeting_date: date,
    meeting_from: startTime,
    meeting_to: endTime,
    meeting_type: meetingType || (meetLink ? "virtual" : "inperson"),
    meeting_link: meetLink || "",
    description: description || "",
    // Not a field in the Outlook add-event form - use the same "suitable"
    // defaults ProjectMeeting.jsx falls back to for a freshly created
    // meeting, so status filters/badges elsewhere in the app still work.
    meeting_status: "scheduled",
    timezone: DEFAULT_TIMEZONE,
    address: "",
    // Full contact objects, matching how they're stored in the DB:
    // [{ id, email, fullName, module }]
    invitees,
  };
  const res = await PostRequest(
    "lead/createMeeting",
    payload,
    false,
    { headers },
    extras,
  );
  return res;
}
export async function updateOutlookMeeting({
  id,
  userId,
  customer_id,
  moduleId,
  eventId,
  moduleName = "calendar",
  title,
  description,
  date,
  startTime,
  endTime,
  meetLink,
  meetingType,
  meetingStatus,
  invitees = [],
  assignTo,
  headers,
  extras,
}) {
  if (!id) {
    throw new Error("updateOutlookMeeting requires the meeting's id");
  }
  if (!userId || !customer_id) {
    throw new Error("updateOutlookMeeting requires userId and customer_id");
  }
  const payload = {
   meeting_id:id,
    module_id: moduleId || userId,
    module_name: "lead",
    customer_id,
    user_login_id: userId,
    assign_to: assignTo || userId,
    meeting_title: title,
    meeting_date: date,
    meeting_from: startTime,
    meeting_to: endTime,
    meeting_type: meetingType || (meetLink ? "virtual" : "inperson"),
    meeting_link: meetLink || "",
    description: description || "",
    event_id: eventId || "",
    // Not a field in the Outlook add-event form - keep it consistent with
    // whatever status the meeting already has instead of blanking it out,
    // since ProjectMeeting.jsx uses this same status elsewhere (badges,
    // filters, "mark as conducted/cancelled" actions).
    meeting_status: meetingStatus || "scheduled",
    timezone: DEFAULT_TIMEZONE,
    address: "",
    // Full contact objects, matching how they're stored in the DB:
    // [{ id, email, fullName, module }]
    invitees,
  };
  const res = await PutRequest(
    `lead/updateMeeting/${encodeURIComponent(id)}`,
    payload,
    { headers },
    extras,
  );
  return res;
}

export async function listOutlookEvents(
  userId,
  headers,
  extras,
  customer_id,
  startDate,
  endDate,
) {
  if (!userId || !customer_id) return [];

  let startDateParam;
  let endDateParam;
  if (startDate) {
    startDateParam =
      typeof startDate === "string" ? startDate : formatDateDMY(startDate);
    const endSource = endDate || startDate;
    endDateParam =
      typeof endSource === "string" ? endSource : formatDateDMY(endSource);
  } else {
    const refDate = new Date();
    const firstDay = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const lastDay = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
    startDateParam = formatDateDMY(firstDay);
    endDateParam = formatDateDMY(lastDay);
  }
  try {
    const res = await GetRequest(
      `lead/fetchMeeting_duration/${customer_id}/${userId}?start_date=${startDateParam}&end_date=${endDateParam}`,
      { headers },
      extras,
    );
    console.log(res?.data,"meetings")
    const data = await res?.data;
    return data
      .map(mapMeetingToCalendarEvent)
      .filter(Boolean);
  } catch (err) {
    console.error("Error fetching Outlook events:", err);
    return [];
  }
}
export async function deleteTeamsEvent(eventId, userId) {
  if (!eventId || !userId) return;
  try {
    await fetch(
      `${API_BASE}/delete_teams/${eventId}?userId=${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  } catch {
    // best-effort cleanup - don't block the UI on this
  }
}